import React, { createContext, useContext, useReducer } from 'react';
import { GameState, LevelData, Difficulty } from '../types';
import { playSound } from '../utils/sounds';

const INITIAL_DIFFICULTY: Difficulty = {
  gridSize: 3,  // Start with 3x3
  obstaclesCount: 0,  // No obstacles at first
  showHints: true,  // Always show hints
};

const initializeGrid = (difficulty: Difficulty) => {
  const { gridSize, obstaclesCount } = difficulty;
  const totalCells = gridSize * gridSize;
  const numbers = Array.from({ length: totalCells - obstaclesCount }, (_, i) => i + 1);
  
  // Create empty grid
  const grid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(0));
  const obstacles = Array(gridSize).fill(null).map(() => Array(gridSize).fill(false));
  
  // Place obstacles randomly
  let remainingObstacles = obstaclesCount;
  while (remainingObstacles > 0) {
    const row = Math.floor(Math.random() * gridSize);
    const col = Math.floor(Math.random() * gridSize);
    if (!obstacles[row][col]) {
      obstacles[row][col] = true;
      remainingObstacles--;
    }
  }
  
  // Place numbers randomly in non-obstacle cells
  let availableCells = [];
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      if (!obstacles[i][j]) {
        availableCells.push([i, j]);
      }
    }
  }
  
  // Shuffle available cells
  for (let i = availableCells.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [availableCells[i], availableCells[j]] = [availableCells[j], availableCells[i]];
  }
  
  // Place numbers in shuffled cells
  for (let i = 0; i < numbers.length; i++) {
    const [row, col] = availableCells[i];
    grid[row][col] = numbers[i];
  }
  
  return { grid, obstacles };
};

const initializedGrid = initializeGrid(INITIAL_DIFFICULTY);

const INITIAL_STATE: GameState = {
  patientId: '',
  sessionId: '',
  ageRange: '',
  currentLevel: 1,
  grid: initializedGrid.grid,
  obstacles: initializedGrid.obstacles,
  currentNumber: 1,
  selectedCells: [],
  errors: 0,
  isComplete: false,
  isPaused: false,
  timeElapsed: 0,
  difficulty: INITIAL_DIFFICULTY,
};

type GameAction =
  | { type: 'START_SESSION'; payload: { patientId: string; sessionId: string; ageRange: string } }
  | { type: 'SELECT_CELL'; payload: { row: number; col: number } }
  | { type: 'INCREMENT_ERROR' }
  | { type: 'TOGGLE_PAUSE' }
  | { type: 'UPDATE_TIME'; payload: number }
  | { type: 'COMPLETE_LEVEL' }
  | { type: 'UPDATE_DIFFICULTY'; payload: Difficulty }
  | { type: 'RESET_LEVEL' }
  | { type: 'RESET_GAME' };

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'START_SESSION':
      return {
        ...state,
        ...action.payload,
        currentLevel: 1,
        errors: 0,
        timeElapsed: 0,
      };
    
    case 'SELECT_CELL':
      const { row, col } = action.payload;
      const value = state.grid[row][col];
      
      // Log the cell selection attempt
      console.log('SELECT_CELL:', {
        position: { row, col },
        value,
        currentNumber: state.currentNumber,
        selectedCells: state.selectedCells
      });

      // Always allow selecting the correct current number
      if (value === state.currentNumber) {
        const newSelectedCells = [...state.selectedCells, [row, col]];
        const nextNumber = state.currentNumber + 1;
        const maxNumber = state.difficulty.gridSize * state.difficulty.gridSize - state.difficulty.obstaclesCount;

        console.log('Move accepted:', {
          currentNumber: state.currentNumber,
          nextNumber,
          maxNumber
        });

        // Check if this completes the level
        const isLevelComplete = nextNumber > maxNumber;
        if (isLevelComplete) {
          playSound.success(); // Play success sound when level is completed
        }

        return {
          ...state,
          selectedCells: newSelectedCells,
          currentNumber: nextNumber,
          isComplete: isLevelComplete,
        };
      }
      
      console.log('Move rejected - wrong number');
      return state;
    
    case 'INCREMENT_ERROR':
      playSound.error(); // Play error sound when mistake is made
      return {
        ...state,
        errors: state.errors + 1,
      };
    
    case 'TOGGLE_PAUSE':
      return {
        ...state,
        isPaused: !state.isPaused,
      };
    
    case 'UPDATE_TIME':
      return {
        ...state,
        timeElapsed: action.payload,
      };
    
    case 'COMPLETE_LEVEL':
      return {
        ...state,
        currentLevel: state.currentLevel + 1,
        isComplete: true,
      };
    
    case 'UPDATE_DIFFICULTY':
      return {
        ...state,
        difficulty: action.payload,
      };
    
    case 'RESET_LEVEL':
      const newGrid = initializeGrid(state.difficulty);
      return {
        ...state,
        grid: newGrid.grid,
        obstacles: newGrid.obstacles,
        currentNumber: 1,
        selectedCells: [],
        errors: 0,
        isComplete: false,
        timeElapsed: 0,
        currentLevel: state.currentLevel + 1, // Increment level
      };
    
    case 'RESET_GAME':
      return INITIAL_STATE;
    
    default:
      return state;
  }
};

const GameContext = createContext<{
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
} | null>(null);

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, INITIAL_STATE);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};